import { Helmet } from 'react-helmet-async'
import { BreadcrumbsJsonLd } from '@/components/seo/Breadcrumbs'
import { motion } from 'framer-motion'
import { Button } from '@/components/ui/button'
import { useNavigate, useLocation } from 'react-router-dom'
import { ArrowRight, CalendarPlus, ArrowLeft } from 'lucide-react'
import { trackEvent } from '@/lib/analytics'
import { SimpleLocationsSection } from '@/components/sections/simple-locations-section'
import { TableOfContents } from '@/components/sections/table-of-contents'

const tocItems = [
  { id: 'introduction-section', title: 'Menstrual & Hormonal Health' },
  { id: 'who-this-is-for', title: 'Who This Is For' },
  { id: 'common-reasons', title: 'Common Reasons People Seek Support' },
  { 
    id: 'how-acupuncture-helps', 
    title: 'How Acupuncture & Chinese Medicine May Help',
    children: [
      { id: 'different-life-stages', title: 'Different Life Stages' },
    ]
  },
  { id: 'what-to-expect', title: 'What to Expect' },
  { id: 'emotional-support', title: 'Emotional & Nervous System Support' },
]

export default function MenstrualHormonalHealthPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const showBackLink = location.state?.fromParent === true
  const showHomepageBackLink = location.state?.fromHomepage === true
  const showAppointmentsBackLink = location.state?.fromAppointmentsPage === true

  const openBooking = (source?: string) => {
    if (source) {
      trackEvent('book_appointment_click', { location: source })
    }
    if ((window as any).openBookingModal) {
      ;(window as any).openBookingModal(source)
    }
  }

  return (
    <main className="min-h-screen bg-gray-50">
      <Helmet>
        <title>Menstrual & Hormonal Health | Raintree Chinese Medicine</title>
        <meta name="description" content="Menstrual and hormonal health support at Raintree Chinese Medicine. Support for painful periods, PMS, perimenopause and cycle-related concerns." />
        <meta property="og:title" content="Menstrual & Hormonal Health | Raintree Chinese Medicine" />
        <meta property="og:description" content="Menstrual and hormonal health support at Raintree Chinese Medicine. Support for painful periods, PMS, perimenopause and cycle-related concerns." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://raintreechinesemedicine.com.au/what-we-help-with/menstrual-hormonal-health" />
      </Helmet>
      <h1 className="sr-only">Menstrual & Hormonal Health</h1>
      <BreadcrumbsJsonLd />
      
      {/* Table of Contents */}
      <TableOfContents items={tocItems} />

      {/* Hero Section */}
      <section className="bg-[rgb(132,137,116)] py-16 lg:py-40 px-4 lg:px-0 relative">
        {showHomepageBackLink && (
          <button
            onClick={() => navigate('/#what-we-help-with-section')}
            className="absolute top-4 left-4 text-white/90 hover:text-white text-xs md:text-sm flex items-center gap-1 transition-colors z-10"
          >
            <ArrowLeft className="h-4 w-4" />
            back to What We Help With
          </button>
        )}
        {showAppointmentsBackLink && !showHomepageBackLink && (
          <button
            onClick={() => navigate('/appointments#areas-of-focus-section')}
            className="absolute top-4 left-4 text-white/90 hover:text-white text-xs md:text-sm flex items-center gap-1 transition-colors z-10"
          >
            <ArrowLeft className="h-4 w-4" />
            back to Appointments and Fees
          </button>
        )}
        {showBackLink && !showHomepageBackLink && !showAppointmentsBackLink && (
          <button
            onClick={() => navigate('/what-we-help-with')}
            className="absolute top-4 left-4 text-white/90 hover:text-white text-xs md:text-sm flex items-center gap-1 transition-colors z-10"
          >
            <ArrowLeft className="h-4 w-4" />
            back to What We Help With
          </button>
        )}
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="max-w-6xl mx-auto"
          >
            {/* Mobile: Stack layout */}
            <div className="lg:hidden text-center">
              <h1 className="text-2xl md:text-3xl mb-4 text-white">
                Menstrual & Hormonal Health
              </h1>
              <p className="text-white/90 sm:w-2/3 mx-auto mb-6 sm:mb-12 text-sm sm:text-base">
                Support for teens and adults with menstrual and hormonal concerns.
              </p>
              <div className="flex flex-col gap-3 mb-4 sm:w-1/2 mx-auto">
                <Button 
                  className="w-full bg-white text-gray-800 hover:bg-gray-100"
                  onClick={() => navigate('/appointments')}
                >
                  <span className="inline-flex items-center gap-2">
                    <CalendarPlus className="h-4 w-4" aria-hidden="true" />
                    Appointment Types & Fees
                  </span>
                </Button>
                <Button 
                  className="w-full bg-white text-gray-800 hover:bg-gray-100"
                  onClick={() => openBooking('menstrual_hero_initial_consult')}
                >
                  <span className="inline-flex items-center gap-2">
                    <CalendarPlus className="h-4 w-4" aria-hidden="true" />
                    Book an Initial Consultation & Treatment
                  </span>
                </Button>
              </div>
              <Button
                variant="outline"
                className="w-full border-white text-white hover:bg-white/10 sm:w-1/2 mx-auto"
                onClick={() => openBooking('menstrual_hero_free_consult')}
              >
                <span className="inline-flex items-center gap-2">
                  <CalendarPlus className="h-4 w-4" aria-hidden="true" />
                  Book a free 15-minute phone consult
                </span>
              </Button>
            </div>

            {/* Desktop: Side-by-side layout */}
            <div className="hidden lg:grid lg:grid-cols-2 lg:gap-12 lg:items-center">
              <div>
                <h1 className="text-3xl lg:text-4xl mb-4 text-white">
                  Menstrual & Hormonal Health
                </h1>
                <p className="text-white/90 text-base lg:text-lg">
                  Cycles are one of the main ways the body speaks. Renee supports teens and adults with menstrual and hormonal concerns.
                </p>
              </div>
              <div className="flex flex-col gap-3 mx-auto px-4">
                <Button 
                  className="w-full bg-white text-gray-800 hover:bg-gray-100"
                  onClick={() => navigate('/appointments')}
                >
                  <span className="inline-flex items-center gap-2">
                    <CalendarPlus className="h-4 w-4" aria-hidden="true" />
                    Appointment Types & Fees
                  </span>
                </Button>
                <Button 
                  className="w-full bg-white text-gray-800 hover:bg-gray-100"
                  onClick={() => openBooking('menstrual_hero_initial_consult')}
                >
                  <span className="inline-flex items-center gap-2">
                    <CalendarPlus className="h-4 w-4" aria-hidden="true" />
                    Book an Initial Consultation & Treatment
                  </span>
                </Button>
                <Button
                  variant="outline"
                  className="w-full border-white text-white hover:bg-white/80"
                  onClick={() => openBooking('menstrual_hero_free_consult')}
                >
                  <span className="inline-flex items-center gap-2">
                    <CalendarPlus className="h-4 w-4" aria-hidden="true" />
                    Book a free 15-minute phone consult
                  </span>
                </Button>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Introduction Section */}
      <section id="introduction-section" className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl mx-auto"
          >
            <h2 className="text-2xl md:text-3xl mb-8 text-[#3f4141] max-w-3xl mx-auto text-center">
              Menstrual & Hormonal Health
            </h2>
            <div className="pl-4 lg:pl-[16%] max-w-3xl">
              <p className="text-gray-600 mb-4 text-left font-medium">
                Cycles are one of the main ways the body speaks.
              </p>
              <p className="text-gray-600 font-medium mb-4 text-left">
                This page is for people who are:
              </p>
              <ul className="list-disc list-inside space-y-2 mb-6 text-gray-600 text-left">
                <li>Living with painful, heavy or irregular periods</li>
                <li>Feeling like hormones are "running the show" each month</li>
                <li>Moving through perimenopause and noticing changes in sleep, mood, bleeding or energy</li>
                <li>Wanting a clearer understanding of what their cycle is doing, and what might genuinely help</li>
              </ul>
              <p className="text-gray-600 text-left">
                Renee offers acupuncture and Chinese herbal medicine to support menstrual and hormonal health across different life stages, alongside your existing medical care.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Who This Is For Section */}
      <section id="who-this-is-for" className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl mx-auto"
          >
            <h2 className="text-2xl md:text-3xl mb-8 text-[#3f4141] max-w-3xl mx-auto text-center">
              Who This Is For
            </h2>
            <div className="pl-4 lg:pl-[16%] max-w-3xl">
              <p className="text-gray-600 font-medium mb-4 text-left">
                People often come for menstrual and hormonal support when they:
              </p>
              <ul className="list-disc list-inside space-y-2 mb-6 text-gray-600 text-left">
                <li>Have periods that are very painful, heavy, long or unpredictable</li>
                <li>Experience strong PMS or PMDD symptoms such as mood swings, irritability, low mood or anxiety before bleeding</li>
                <li>Have been told they may have endometriosis, PCOS, or other hormonal conditions and want additional support</li>
                <li>Notice that headaches, migraines, bowel changes or insomnia cluster around certain points in their cycle</li>
                <li>Are moving into perimenopause and feeling unsettled by new symptoms or changes in bleeding</li>
                <li>Feel dismissed, not believed, or unsure how to make sense of what is happening with their hormones</li>
              </ul>
              <p className="text-gray-600 text-left">
                You don't need to have a formal diagnosis to seek support here. It's also completely okay if you do have a diagnosis and want another layer of care around what you're already doing.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Common Reasons People Seek Support Section */}
      <section id="common-reasons" className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl mx-auto"
          >
            <h2 className="text-2xl md:text-3xl mb-8 text-[#3f4141] max-w-3xl mx-auto text-center">
              Common Reasons People Seek Support
            </h2>
            <div className="pl-4 lg:pl-[16%] max-w-3xl">
              <p className="text-gray-600 font-medium mb-4 text-left">
                Some of the patterns Renee commonly sees include:
              </p>
              <ul className="list-disc list-inside space-y-2 mb-6 text-gray-600 text-left">
                <li>Periods that are very painful or require regular pain medication</li>
                <li>Heavy bleeding, flooding, clotting or needing to change products very frequently</li>
                <li>Cycles that are very short, very long or highly irregular</li>
                <li>PMS or PMDD symptoms such as marked mood swings, tearfulness, anger, anxiety, breast tenderness or bloating</li>
                <li>Perimenopausal symptoms such as hot flushes, night sweats, sleep disturbance, mood shifts or cycle changes</li>
                <li>Headaches or migraines that reliably appear at particular points in the cycle</li>
                <li>Digestive changes around bleeding – loose bowels, constipation or nausea</li>
              </ul>
              <p className="text-gray-600 text-left">
                Every person's pattern is different. Part of the work is mapping your symptoms over time, rather than looking at a single month in isolation.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* How Acupuncture & Chinese Medicine May Help Section */}
      <section id="how-acupuncture-helps" className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl mx-auto"
          >
            <h2 className="text-2xl md:text-3xl mb-8 text-[#3f4141] max-w-3xl mx-auto text-center">
              How Acupuncture & Chinese Medicine May Help
            </h2>
            <div className="pl-4 lg:pl-[16%] max-w-3xl">
              <p className="text-gray-600 font-medium mb-4 text-left">
                Within menstrual and hormonal care, acupuncture and Chinese medicine are used to:
              </p>
              <ul className="list-disc list-inside space-y-2 mb-6 text-gray-600 text-left">
                <li>Look at the cycle as a whole – length, quality of bleeding, pain, mood and energy over the month</li>
                <li>Support the nervous system, which can strongly influence how hormones feel in daily life</li>
                <li>Address sleep, digestion and stress, which often sit underneath or around hormonal symptoms</li>
                <li>Offer practical, step-by-step changes that are realistic for your current life</li>
              </ul>
              <p className="text-gray-600 text-left">
                Treatment is tailored to your age, life stage and priorities. It is always intended to sit <strong>alongside</strong>, not replace, the care you receive from your GP, gynaecologist, endocrinologist or other health providers.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Different Life Stages Section */}
      <section id="different-life-stages" className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl mx-auto"
          >
            <h2 className="text-2xl md:text-3xl mb-8 text-[#3f4141] max-w-3xl mx-auto text-center">
              Different Life Stages
            </h2>
            
            {/* First Periods & Teens */}
            <div className="mb-12">
              <div className="pl-4 lg:pl-[16%] max-w-3xl">
                <h3 className="text-xl md:text-2xl mb-6 text-[#3f4141] text-left font-medium">
                  First Periods & Teens
                </h3>
                <p className="text-gray-600 mb-4 text-left font-medium">
                  Early cycles can be confusing – for the person having them and for the adults around them.
                </p>
                <p className="text-gray-600 font-medium mb-4 text-left">
                  Support at this stage may focus on:
                </p>
                <ul className="list-disc list-inside space-y-2 mb-6 text-gray-600 text-left">
                  <li>Painful periods or cramping that interfere with school, sport or social life</li>
                  <li>Heavy or irregular bleeding</li>
                  <li>Mood changes or anxiety that cluster around the period</li>
                  <li>Helping the young person understand what's happening in a clear, calm and age-appropriate way</li>
                </ul>
                <p className="text-gray-600 text-left">
                  If a teen or young adult is already under the care of a GP, paediatrician or gynaecologist, treatment can be planned with that in mind.
                </p>
              </div>
            </div>

            {/* Reproductive Years */}
            <div className="mb-12">
              <div className="pl-4 lg:pl-[16%] max-w-3xl">
                <h3 className="text-xl md:text-2xl mb-6 text-[#3f4141] text-left font-medium">
                  Reproductive Years
                </h3>
                <p className="text-gray-600 font-medium mb-4 text-left">
                  During the main reproductive years, menstrual and hormonal care may involve:
                </p>
                <ul className="list-disc list-inside space-y-2 mb-6 text-gray-600 text-left">
                  <li>Cycles that feel too painful, too heavy, too frequent or too unpredictable</li>
                  <li>Conditions such as suspected or diagnosed PCOS or endometriosis symptoms</li>
                  <li>Cycle-related headaches, bowel changes, breast pain or energy dips</li>
                  <li>PMS/PMDD patterns that impact work, relationships or day-to-day life</li>
                </ul>
                <p className="text-gray-600 text-left">
                  For some, menstrual health is also part of preparing for pregnancy or supporting fertility. For others, pregnancy is not part of the picture and the focus is simply on feeling more stable and in control of their cycle.
                </p>
              </div>
            </div>

            {/* Perimenopause & Later Life */}
            <div>
              <div className="pl-4 lg:pl-[16%] max-w-3xl">
                <h3 className="text-xl md:text-2xl mb-6 text-[#3f4141] text-left font-medium">
                  Perimenopause & Later Life
                </h3>
                <p className="text-gray-600 mb-4 text-left font-medium">
                  Perimenopause is a long transition, not a single moment. It can bring:
                </p>
                <ul className="list-disc list-inside space-y-2 mb-6 text-gray-600 text-left">
                  <li>Changes to bleeding – lighter, heavier, closer together or further apart</li>
                  <li>Hot flushes, night sweats and sleep disruption</li>
                  <li>Mood shifts, irritability or increased anxiety</li>
                  <li>New digestive or pain patterns that seem to "arrive with the hormones"</li>
                </ul>
                <p className="text-gray-600 font-medium mb-4 text-left">
                  Support at this stage may involve:
                </p>
                <ul className="list-disc list-inside space-y-2 mb-6 text-gray-600 text-left">
                  <li>Tracking changes in bleeding, sleep and symptoms over time</li>
                  <li>Using acupuncture and, where appropriate, Chinese herbal medicine to support steadier sleep, mood and energy</li>
                  <li>Offering space to talk through the emotional side of this transition</li>
                </ul>
                <p className="text-gray-600 text-left">
                  Treatment can sit alongside HRT, non-hormonal medications or other approaches, depending on what you and your doctor decide together.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* What to Expect Section */}
      <section id="what-to-expect" className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl mx-auto"
          >
            <h2 className="text-2xl md:text-3xl mb-8 text-[#3f4141] max-w-3xl mx-auto text-center">
              What to Expect in a Menstrual & Hormonal Appointment
            </h2>
            
            {/* Your First Visit */}
            <div className="mb-12">
              <div className="pl-4 lg:pl-[16%] max-w-3xl">
                <h3 className="text-xl md:text-2xl mb-6 text-[#3f4141] text-left font-medium">
                  Your First Visit
                </h3>
                <p className="text-gray-600 mb-4 text-left font-medium">
                  Your initial consultation and treatment is a longer appointment designed to give time to understand the full picture.
                </p>
                <p className="text-gray-600 font-medium mb-4 text-left">
                  Together you will:
                </p>
                <ul className="list-disc list-inside space-y-2 mb-6 text-gray-600 text-left">
                  <li>Discuss your menstrual and hormonal history in detail, including past treatments and investigations</li>
                  <li>Map your current cycle: timing, bleeding, pain, mood, sleep, digestion and any associated symptoms</li>
                  <li>Talk about other parts of your health that may be relevant – stress levels, work, caring responsibilities, previous pregnancies or losses</li>
                  <li>Begin acupuncture treatment, and, where appropriate, discuss Chinese herbal medicine and simple lifestyle strategies</li>
                </ul>
                <p className="text-gray-600 text-left">
                  You won't be asked to change everything at once. The aim is to gain clarity about what is happening and agree on some realistic next steps.
                </p>
              </div>
            </div>

            {/* Ongoing Care */}
            <div>
              <div className="pl-4 lg:pl-[16%] max-w-3xl">
                <h3 className="text-xl md:text-2xl mb-6 text-[#3f4141] text-left font-medium">
                  Ongoing Care
                </h3>
                <p className="text-gray-600 font-medium mb-4 text-left">
                  Follow-up appointments focus on:
                </p>
                <ul className="list-disc list-inside space-y-2 mb-6 text-gray-600 text-left">
                  <li>Checking in on changes in pain, bleeding, mood and energy since your last visit</li>
                  <li>Adjusting acupuncture points and any herbal formulas in response to your cycle phase and symptoms</li>
                  <li>Reviewing how often it makes sense to come in – weekly, fortnightly, monthly or in blocks around specific phases</li>
                  <li>Continuing to integrate what you are doing here with any medical treatments or investigations you are undergoing</li>
                </ul>
                <p className="text-gray-600 text-left">
                  Plans are always collaborative. You and Renee will decide together what feels sustainable and appropriate as things change.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Emotional & Nervous System Support Section */}
      <section id="emotional-support" className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl mx-auto"
          >
            <h2 className="text-2xl md:text-3xl mb-8 text-[#3f4141] max-w-3xl mx-auto text-center">
              Emotional & Nervous System Support
            </h2>
            <div className="pl-4 lg:pl-[16%] max-w-3xl">
              <p className="text-gray-600 mb-4 text-left font-medium">
                Hormonal changes do not happen in isolation from life.
              </p>
              <p className="text-gray-600 font-medium mb-4 text-left">
                Many people describe:
              </p>
              <ul className="list-disc list-inside space-y-2 mb-6 text-gray-600 text-left">
                <li>Feeling like they are "a different person" for part of each month</li>
                <li>Worrying about how their mood or energy affects family, partners, children or colleagues</li>
                <li>Feeling dismissed or told that symptoms are "just part of being a woman"</li>
                <li>Carrying frustration, shame or grief about how their body behaves</li>
              </ul>
              <p className="text-gray-600 font-medium mb-4 text-left">
                Sessions are a space to:
              </p>
              <ul className="list-disc list-inside space-y-2 mb-6 text-gray-600 text-left">
                <li>Slow down and be with what you are experiencing, rather than pushing through it</li>
                <li>Allow the nervous system to settle during acupuncture</li>
                <li>Talk about what is actually happening in a way that feels grounded and respectful</li>
              </ul>
              <p className="text-gray-600 text-left">
                While acupuncture and Chinese medicine are not counselling, they can sit alongside psychological support and help your system find more steadiness as you work through hormonal changes.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Is This Approach Right for You? Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl mx-auto"
          >
            <h2 className="text-2xl md:text-3xl mb-8 text-[#3f4141] max-w-3xl mx-auto text-center">
              Is This Approach Right for You?
            </h2>
            <div className="pl-4 lg:pl-[16%] max-w-3xl">
              <p className="text-gray-600 font-medium mb-4 text-left">
                Menstrual and hormonal support at Raintree may be a good fit if you:
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-600 mb-8 text-left">
                <li>Want your symptoms taken seriously and looked at over time, not just in a single appointment</li>
                <li>Prefer a calm, steady approach rather than quick-fix promises</li>
                <li>Are happy to have treatment that works <strong>with</strong> your GP, gynaecologist or other specialists</li>
                <li>Are open to the idea that stress, sleep and digestion are part of the overall hormonal picture</li>
              </ul>
              <p className="text-gray-600 font-medium mb-4 text-left">
                If you're unsure, you don't have to decide immediately.
              </p>
              <p className="text-gray-600 text-left">
                You're welcome to book a{' '}
                <button
                  onClick={() => openBooking('menstrual_approach_free_consult')}
                  className="underline font-normal text-gray-600 hover:text-gray-800 cursor-pointer"
                >
                  free 15-minute phone consult
                </button>
                {' '}to talk briefly with Renee about what you're experiencing and ask any initial questions.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Appointments, Locations & How to Start */}
      <SimpleLocationsSection onBook={(locationName) => {
        trackEvent('book_appointment_click', { location: `menstrual_locations_${locationName.toLowerCase()}` })
        openBooking(`menstrual_locations_${locationName.toLowerCase()}`)
      }} />

      {/* Next Steps CTA Section */}
      <section className="py-16 lg:py-32 bg-white px-4 lg:px-0">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="max-w-6xl mx-auto"
          >
            {/* Mobile: Stack layout */}
            <div className="lg:hidden text-center">
              <h2 className="text-2xl md:text-xl mb-10 text-[#3f4141]">
                Next steps
              </h2>
              <div className="mb-6">
                <p className="text-gray-600 mb-4">
                  For new menstrual or hormonal patients, the best starting point is:
                </p>
                <ul className="list-disc list-inside space-y-2 text-gray-600 mb-4 inline-block text-left max-w-lg mx-auto">
                  <li>
                    <strong>Initial Consultation & Treatment</strong> – a longer first appointment to take a full history, map your cycle and begin treatment.
                  </li>
                </ul>
                <p className="text-gray-600 max-w-lg mx-auto">
                  You can then decide together how often to continue and which clinic is most practical for you.
                </p>
              </div>
              <div className="flex flex-col gap-3 mb-4 sm:w-1/2 mx-auto">
                <Button
                  className="w-full bg-[rgb(132,137,116)] text-white hover:bg-[rgb(110,115,95)]"
                  onClick={() => {
                    trackEvent('book_appointment_click', { location: 'menstrual_cta_view_appointments' })
                    navigate('/appointments')
                  }}
                >
                  <span className="inline-flex items-center gap-2">
                    Appointment Types & Fees
                    <ArrowRight className="h-4 w-4" />
                  </span>
                </Button>
                <Button
                  className="w-full bg-[rgb(132,137,116)] text-white hover:bg-[rgb(110,115,95)]"
                  onClick={() => openBooking('menstrual_cta_initial_consult')}
                >
                  <span className="inline-flex items-center gap-2">
                    <CalendarPlus className="h-4 w-4" aria-hidden="true" />
                    Book Initial Consult & Treatment
                  </span>
                </Button>
              </div>
              <Button
                variant="outline"
                className="w-full border-gray-600 text-gray-600 hover:bg-gray-50 sm:w-1/2 mx-auto"
                onClick={() => openBooking('menstrual_cta_free_consult')}
              >
                <span className="inline-flex items-center gap-2">
                  <CalendarPlus className="h-4 w-4" aria-hidden="true" />
                  Free 15-minute phone consult
                </span>
              </Button>
            </div>

            {/* Desktop: Title centered above two columns */}
            <div className="hidden lg:block">
              <h2 className="text-3xl lg:text-4xl mb-16 text-[#3f4141] text-center">
                Next steps
              </h2>
              <div className="lg:grid lg:grid-cols-2 lg:gap-12 lg:items-center">
                <div>
                  <p className="text-gray-600 mb-4 text-base lg:text-md">
                    For new menstrual or hormonal patients, the best starting point is:
                  </p>
                  <ul className="list-disc list-inside space-y-2 text-gray-600 mb-4 text-base lg:text-md">
                    <li>
                      <strong>Initial Consultation & Treatment</strong> – a longer first appointment to take a full history, map your cycle and begin treatment.
                    </li>
                  </ul>
                  <p className="text-gray-600 text-base lg:text-md">
                    You can then decide together how often to continue and which clinic is most practical for you.
                  </p>
                </div>
                <div className="flex flex-col gap-3 mx-auto px-4 w-80">
                  <Button 
                    className="w-full bg-[rgb(132,137,116)] text-white hover:bg-[rgb(110,115,95)]"
                    onClick={() => {
                      trackEvent('book_appointment_click', { location: 'menstrual_cta_view_appointments' })
                      navigate('/appointments')
                    }}
                  >
                    <span className="inline-flex items-center gap-2">
                      Appointment Types & Fees
                      <ArrowRight className="h-4 w-4" />
                    </span>
                  </Button>
                  <Button 
                    className="w-full bg-[rgb(132,137,116)] text-white hover:bg-[rgb(110,115,95)]"
                    onClick={() => openBooking('menstrual_cta_initial_consult')}
                  >
                    <span className="inline-flex items-center gap-2">
                      <CalendarPlus className="h-4 w-4" aria-hidden="true" />
                      Book Initial Consult & Treatment
                    </span>
                  </Button>
                  <Button
                    variant="outline"
                    className="w-full border-gray-600 text-gray-600 hover:bg-gray-50"
                    onClick={() => openBooking('menstrual_cta_free_consult')}
                  >
                    <span className="inline-flex items-center gap-2">
                      <CalendarPlus className="h-4 w-4" aria-hidden="true" />
                      Free 15-minute phone consult
                    </span>
                  </Button>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

    </main>
  )
}

